import { DialogBody, Dialog, Tooltip } from '@material-tailwind/react'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import React, { useEffect, useState } from 'react'
import { cx } from 'class-variance-authority'
import { NewInput } from '../ui/form/inputs/NewInput'
import { buttonClassName } from '../ui/button/utils'
import { BaseLoader } from '../base/BaseLoader'
import { postNewClientNotificationSubscription } from '../../services/notificationSubscriptions/newClient.service'
import { useNotificationSubscriptionStore } from '../../stores/notification/notificationSubscriptionStore'

export const NewClientNotificationSubscriptionModal = ({
  backendUrl,
  frontStrapiToken,
}: {
  backendUrl: string
  frontStrapiToken: string
}) => {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  const {
    isNewClientNotificationSubscriptionModalOpen,
    toggleNewClientNotificationSubscriptionModal,
    email,
    errors,
    isDirty,
    setEmail,
    validateForm,
    resetState,
    firmUuid,
  } = useNotificationSubscriptionStore()

  const close = () => {
    toggleNewClientNotificationSubscriptionModal()
    resetState()
    setSuccess(false)
  }

  useEffect(() => {
    if (isDirty) {
      validateForm()
    }
  }, [email, isDirty])

  const isValid = !errors.email

  const handleSubmit = async () => {
    try {
      setLoading(true)
      await postNewClientNotificationSubscription({
        email,
        firmUuid,
        backendUrl,
        frontStrapiToken,
      })
      setSuccess(true)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={isNewClientNotificationSubscriptionModalOpen}
      handler={() => close()}
      size="md"
      className="relative"
    >
      <DialogBody className="">
        <button
          type="button"
          className="absolute transition-transform duration-700 ease-in-out top-4 right-4 hover:rotate-90 hover:text-night"
          onClick={close}
        >
          <XMarkIcon className="w-6 h-6" />
        </button>

        <h2 className="font-semibold text-night text-base/7">
          Alertez-moi lorsqu&apos;il sera référencé
        </h2>

        <form className="flex flex-col justify-between mt-6">
          <div
            className={cx(
              'absolute top-0 left-0 right-0 bottom-0 z-10 bg-white flex',
              !success && 'hidden',
            )}
          >
            <div className="flex flex-col items-center justify-center flex-1 space-y-4">
              <div className="flex items-center justify-center">
                <div className="flex items-center justify-center p-3 mx-auto bg-green-100 rounded-full">
                  <CheckIcon
                    aria-hidden="true"
                    className="w-8 h-8 text-green-600"
                  />
                </div>
              </div>
              <div className="mt-3 text-center sm:mt-5">
                <h3 className="text-base font-semibold text-night">
                  Votre demande a bien été envoyée
                </h3>
              </div>

              <button
                type="button"
                className={buttonClassName({
                  color: 'primary',
                  invert: true,
                  disabled: loading,
                  size: 'medium',
                })}
                onClick={() => {
                  resetState()
                  close()
                  setSuccess(false)
                }}
              >
                Fermer
              </button>
            </div>
          </div>
          <NewInput
            value={email}
            name="email"
            label="Email"
            placeholder="Mika@exemple.com"
            autoComplete="email"
            type="email"
            onChange={(value) => {
              setEmail(value)
            }}
            error={errors?.email}
          />

          <div className="flex items-center justify-center px-4 py-2 mt-4 border-t md:justify-end gap-x-6 border-gray-900/10 sm:px-8">
            <button
              type="button"
              className="font-semibold text-night text-sm/6"
              title="Annuler"
              onClick={() => {
                close()
              }}
            >
              Annuler
            </button>
            <Tooltip
              className="px-4 py-3 border shadow-xl border-lamacompta-blue bg-very-light-blue shadow-black/10"
              content={
                <div className="space-y-1">
                  <p className="text-sm text-center text-night">
                    {!isValid ? 'Veuillez corriger les erreurs' : 'Enregistrer'}
                  </p>
                </div>
              }
            >
              <button
                type="button"
                className={buttonClassName({
                  color: 'primary',
                  invert: true,
                  disabled: !isValid || loading,
                  size: 'medium',
                })}
                title={
                  !isValid ? 'Veuillez compléter le formulaire' : 'Enregistrer'
                }
                disabled={!isValid || loading || !email}
                onClick={async (e) => {
                  e.preventDefault()
                  await handleSubmit()
                }}
              >
                {!loading ? 'Enregistrer' : <BaseLoader />}
              </button>
            </Tooltip>
          </div>
        </form>
      </DialogBody>
    </Dialog>
  )
}
